import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  DatasetDataInfoResponse,
  DatasetQueryParams,
} from "api/model/dataset/DatasetDataInfoResponse";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import chunk from "lodash-es/chunk";
import { useMemo } from "react";
import { APIResponse } from "services/HttpService";
import { DatasetDataApi } from "../DatasetDataApi";
import DatasetsApi from "../DatasetsApi";
import { BaseDatasetDTO } from "../model/dataset/BaseDatasetDTO";
import { DatasetDataLagLeadRequest } from "../model/requests";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";
import { ChronoUnit, Instant } from "@js-joda/core";

export const useGetDatasetCategories = () =>
  useQueryWrapper([QueryKey.DATASET_CATEGORIES], () => DatasetsApi.getDatasetCategories());

export const useGetDatasetInfo = (identifier: string | null, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, identifier],
    () => DatasetsApi.getDatasetInfo(identifier),
    null,
    options
  );

export const useGetDatasetsInfo = (identifiers: Array<string | null>, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, identifiers.join(",")],
    () => DatasetsApi.getDatasetsInfo(identifiers),
    null,
    options
  );

export const useGetDatasetsInfoBatched = (
  identifiers: Array<string | null>,
  options?: IQueryOptions,
  batchSize: number = 25
) => {
  const chunks = useMemo(
    () => chunk(identifiers.filter(Boolean) as string[], batchSize),
    [identifiers, batchSize]
  );

  const queries = useQueries({
    queries: chunks.map(currentChunk => ({
      queryKey: [QueryKey.DATASET_INFOS, currentChunk.join(",")],
      queryFn: () => DatasetsApi.getDatasetsInfo(currentChunk),
      ...options,
      refetchInterval: 0,
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);
  const data = queries.flatMap(q => q.data ?? []);

  return { data, isLoading, isError };
};

export const useSearchDatasets = (queryParams: DatasetQueryParams, options?: IQueryOptions) =>
  useQueryWrapper(
    [
      QueryKey.DATASET_INFOS,
      queryParams.query,
      queryParams.limit?.toString(),
      queryParams.identifiers?.toString(),
    ],
    async () => {
      const data = await DatasetsApi.queryDatasets(queryParams);
      if (queryParams.proxied) {
        const regData = await DatasetsApi.queryDatasets({ ...queryParams, proxied: false });
        return {
          ...data,
          data: [...(data.data ?? []), ...(regData.data ?? [])],
        };
      }
      return data;
    },
    null,
    options
  );

export const useGetBaseDataset = (identifier: string | null, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.BASE_DATASETS, identifier],
    () => DatasetsApi.getBaseDataset(identifier),
    null,
    options
  );

export const useGetPaginatedDatasets = (params: DatasetQueryParams, options?: IQueryOptions) => {
  const { query, page, limit, identifiers, companyRef, proxied } = params;
  return useQueryWrapper(
    [
      QueryKey.PAGINATED_DATASETS,
      query ?? identifiers?.join(","),
      `${page}[${limit}]`,
      companyRef,
      proxied?.toString(),
    ],
    () => DatasetsApi.queryDatasets({ ...params, allowEmptyQuery: true }),
    null,
    options
  );
};

export const useGetDatasetsCount = (options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.DATASETS_COUNT], () => DatasetsApi.getDatasetsCount(), null, options);

export const useGetDatasetDataInfo = (
  identifiers: Array<string>,
  config?: DatasetDataLagLeadRequest,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [QueryKey.DATASET_DATA_INFO, ...identifiers],
    () => DatasetDataApi.getDataInfo(identifiers, config),
    null,
    options
  );

export const useGetDataInfoRange = (
  identifiers: Array<string>,
  params: {
    uniqueKey?: string;
    timeStart: string;
    timeEnd?: string;
    downsampleInterval?: number;
    useDefaultDownsampleInterval?: boolean;
    usePromiseHandler?: ((res: APIResponse<DatasetDataInfoResponse[]>) => any) | null | undefined;
  },
  options?: IQueryOptions
) => {
  let currentDownsampleInterval: number | undefined = params.downsampleInterval;

  if (currentDownsampleInterval == null && params.useDefaultDownsampleInterval) {
    const from = Instant.parse(params.timeStart);
    const to = params.timeEnd ? Instant.parse(params.timeEnd) : from;
    const intervalFromToInSeconds = from.until(to, ChronoUnit.SECONDS);
    const maxPoints = 500;
    currentDownsampleInterval = Math.round(intervalFromToInSeconds / maxPoints);
  }

  return useQueryWrapper(
    [
      QueryKey.DATASET_DATA_INFO,
      params.uniqueKey,
      ...identifiers,
      params.timeStart,
      params.timeEnd || params.timeStart,
    ],
    () =>
      DatasetDataApi.getDataInfoRange(
        identifiers,
        params.timeStart,
        params.timeEnd,
        currentDownsampleInterval
      ),
    params.usePromiseHandler,
    options
  );
};

export const useGetDataInfoRangeV2 = (
  identifiers: Array<string>,
  params: {
    uniqueKey?: string;
    timeStart: string;
    timeEnd?: string;
    downsampleInterval?: number;
    usePromiseHandler?: ((res: APIResponse<DatasetDataInfoResponse[]>) => any) | null | undefined;
  },
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [
      QueryKey.DATASET_DATA_INFO,
      params.uniqueKey,
      ...identifiers,
      params.timeStart,
      params.timeEnd || params.timeStart,
    ],
    () =>
      DatasetDataApi.getDataInfoRangeV2(
        identifiers,
        params.timeStart,
        params.timeEnd,
        params.downsampleInterval
      ),
    params.usePromiseHandler,
    options
  );

export const useGetRecentData = (
  identifiers: Array<string>,
  options: {
    count?: number;
    options?: IQueryOptions;
    refTime?: string;
    usePromiseHandler?:
      | ((res: APIResponse<Array<DatasetDataInfoResponse>>) => any)
      | null
      | undefined;
  }
) =>
  useQueryWrapper(
    [QueryKey.DATASET_DATA_RECENT, ...identifiers, options.count?.toString(), options.refTime],
    () =>
      DatasetDataApi.getRecentData(identifiers, {
        count: options.count,
        refTime: options.refTime,
      }),
    options.usePromiseHandler,
    options.options
  );

export const useGetRecentDataBatched = (
  identifiers: Array<string>,
  options: {
    count?: number;
    options?: IQueryOptions;
    refTime?: string;
    usePromiseHandler?:
      | ((res: APIResponse<Array<DatasetDataInfoResponse>>) => any)
      | null
      | undefined;
  },
  batchSize: number = 25
) => {
  const chunks = useMemo(
    () => chunk(identifiers.filter(Boolean) as string[], batchSize),
    [identifiers, batchSize]
  );

  const getRefetchInterval = () => {
    const refetchInterval = options.options?.refetchInterval;
    if (typeof refetchInterval === "boolean") {
      return refetchInterval && 5000;
    }
    return refetchInterval;
  };

  const queries = useQueries({
    queries: chunks.map(currentChunk => ({
      queryKey: [QueryKey.DATASET_DATA_RECENT, currentChunk.join(",")],
      queryFn: () =>
        DatasetDataApi.getRecentData(currentChunk, {
          count: options.count,
          refTime: options.refTime,
        }),
      refetchInterval: getRefetchInterval(),
      ...options,
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);
  const data = queries.flatMap(q => q.data ?? []);

  return { data, isLoading, isError };
};

export const useGetDefaultDatasetCategory = () =>
  useQueryWrapper([QueryKey.DEFAULT_CATEGORY], () => DatasetsApi.getDefaultDatasetCategory());

export const useSearchDatasetsUsage = (identifiers: string[], options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_USAGE_SEARCH, identifiers.toString()],
    () => DatasetsApi.searchDatasetsUsage(identifiers),
    null,
    options
  );

export const useClearDatasetData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queryData: {
      identifiers: Array<String>;
      timeStart: string;
      timeEnd: string;
      clearAll: boolean;
    }) => DatasetDataApi.clearDatasetData(queryData),
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.CLEAR_DATASET], () => {
        return data;
      });
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useCreateCategorizedDataset = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<BaseDatasetDTO>) =>
      DatasetsApi.createNewCategorizedDataset(data).then(defaultResponseHandler),
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.CATEGORIZED_DATASET], () => {
        return data;
      });
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useUpdateDatasetInfo = (fullUpdate?: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: BaseDatasetDTO) => {
      const res = await DatasetsApi.putUpdateDatasetInfo(data, fullUpdate);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueriesData(
        { queryKey: [QueryKey.PAGINATED_DATASETS], type: "active" },
        (oldQueryData: DatasetInfo[] | undefined) => {
          if (!data) return oldQueryData;
          return oldQueryData?.map(dataset =>
            dataset.identifier === data.identifier ? data : dataset
          ) as DatasetInfo[];
        }
      );
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DatasetsApi.deleteDataset(id).then(defaultResponseHandler),
    ...defaultMutationOptions,
    onSettled: () => {
      queryClient.removeQueries();
    },
  });
};

export const useHardDeleteDataset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DatasetsApi.hardDeleteDataset(id).then(defaultResponseHandler),
    ...defaultMutationOptions,
    onSettled: () => {
      queryClient.removeQueries();
    },
  });
};

export const useGetDatasetsInfoByTags = (
  categoryId: number | undefined,
  tags: string[],
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, categoryId, tags.join(",")],
    () => DatasetsApi.getDatasetsByTags(categoryId, tags.join(",")),
    null,
    options
  );
